import { MediaDevice, useStore } from '@/lib/store'
import {
  NotificationItem,
  NotificationItemVariant,
} from '@/lib/models/NotificationItem'
import { Call, Device } from '@twilio/voice-sdk'
import { useMutation, useQuery } from '@tanstack/react-query'
import { GetTwilioToken } from '@/lib/apis/twilio'
import { user } from '@/lib/utils'
import { DOCTOR_ROLE } from '@/lib/constants'
import { Session } from 'next-auth'
import useApiClient from '@/lib/apis/clients/useApiClient'
import { RecordCall } from '@/lib/apis/consultations' // Currently this dude's responsibility is too much, and need to shift some of them into container instead
import { useEffect } from 'react'
import {
  captureTwilioWidgetError,
  TwilioWidgetErrorSource,
} from '@/lib/twilio-widget-errors'

// Currently this dude's responsibility is too much, and need to shift some of them into container instead
export function useTwilioWidget(
  session: Session | null,
  onCallEnd?: () => Promise<void>,
) {
  const api = useApiClient()
  const { contains } = user(session)
  const twilioState = useStore((state) => state.twilioWidgetState)
  const setTwilioState = useStore((state) => state.setTwilioState)
  const showNotification = useStore((state) => state.showNotification)
  const twilioContext = useStore((state) => state.twilioContext)
  const completeTwilioContext = useStore((state) => state.completeTwilioSetup)
  const setTwilioCallState = useStore((state) => state.setTwilioCallState)
  const tokenQuery = useQuery({
    ...GetTwilioToken.query(api),
    enabled: contains(DOCTOR_ROLE),
  })
  const recordCallMutation = useMutation(RecordCall.mutation(api))

  const allowCall = !twilioContext?.call && tokenQuery.data

  useEffect(() => {
    if (!tokenQuery.error) return

    captureTwilioWidgetError(tokenQuery.error, {
      error_source: 'token_query_failed',
      has_active_call: !!useStore.getState().twilioContext?.call,
    })
  }, [tokenQuery.error])

  async function setupTwilioContext() {
    const audioContext = new AudioContext()
    const device = new Device(tokenQuery.data!!.token, {
      tokenRefreshMs: 60 * 1000, // 1 minute before the token expires
      logLevel: 0,
      codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
    })

    device.on('tokenWillExpire', () => {
      tokenQuery.refetch().then((token) => {
        if (!token.data) {
          captureTwilioWidgetError(
            token.error ?? new Error('Failed to fetch Twilio token'),
            {
              error_source: 'token_refresh_failed',
              device_state: device.state,
              has_active_call: !!useStore.getState().twilioContext?.call,
            },
          )
          showNotification(
            new NotificationItem(
              'Something went wrong with the server, please refresh the page',
              NotificationItemVariant.Error,
            ),
          )
          setTwilioState({ state: 'HIDDEN' })
        } else {
          try {
            device.updateToken(token.data!!.token)
          } catch (error) {
            captureTwilioWidgetError(error, {
              error_source: 'token_update_failed',
              device_state: device.state,
              has_active_call: !!useStore.getState().twilioContext?.call,
            })
          }
        }
      })
    })

    device.on('error', async (error, call) => {
      const currentCall = useStore.getState().twilioContext?.call
      captureTwilioWidgetError(error, {
        error_source: 'device_error',
        call_status:
          call?.status() ??
          (currentCall?.status === 'CONNECTED'
            ? currentCall.call.status()
            : undefined),
        device_state: device.state,
        has_active_call: !!currentCall,
        has_call_from_error: !!call,
      })

      if (currentCall || call) {
        showNotification(
          new NotificationItem(
            'The call connection dropped. Check your internet connection, then try again.',
            NotificationItemVariant.Error,
          ),
        )
      }

      try {
        if (call) {
          await disconnectCall(call)
        } else if (currentCall?.status === 'CONNECTED') {
          await disconnectCall(currentCall.call)
        } else if (currentCall) {
          await resetCallState()
        }
      } catch (cleanupError) {
        captureTwilioWidgetError(cleanupError, {
          error_source: 'device_error_cleanup_failed',
          call_status:
            call?.status() ??
            (currentCall?.status === 'CONNECTED'
              ? currentCall.call.status()
              : undefined),
          device_state: device.state,
          has_active_call: !!currentCall,
          has_call_from_error: !!call,
        })
      }
    })

    const devices = await navigator.mediaDevices.enumerateDevices()

    // Setup input and output devices
    let inputDevices: MediaDevice[] = []
    let outputDevices: MediaDevice[] = []

    for (const device of devices) {
      // I don't need video input for now
      if (device.kind === 'videoinput') continue

      const isDefault = device.deviceId === 'default'
      const target = device.kind === 'audioinput' ? inputDevices : outputDevices
      const label = device.label.replace(/\([0-9a-z:]*\)$/, '')

      // Chrome's oddity, there can be duplicate devices with the first (default) one with id as 'default'.
      // Need to dedupe this.
      if (target.length > 0 && target[0].groupId === device.groupId) {
        target[0].deviceId = device.deviceId
        target[0].name = label
      } else {
        target.push({
          type: device.kind,
          deviceId: device.deviceId,
          groupId: device.groupId,
          name: label,
          default: isDefault,
        })
      }
    }

    completeTwilioContext(
      audioContext,
      device,
      inputDevices,
      inputDevices[0],
      outputDevices,
      outputDevices.length > 0 ? outputDevices[0] : null,
    )

    return device
  }

  async function call(number: string, forConsultation: string) {
    if (!number.match(/[0-9]{10}/)) {
      const error = new Error('Invalid Twilio call phone number')
      captureTwilioWidgetError(error, {
        error_source: 'invalid_phone_number',
        has_active_call: !!twilioContext?.call,
      })
      throw error
    }
    if (twilioContext?.call) {
      const error = new Error(
        "You can't make another call while a call is in-progress",
      )
      captureTwilioWidgetError(error, {
        error_source: 'call_already_in_progress',
        has_active_call: true,
      })
      throw error
    }

    if (!twilioContext) {
      setTwilioState({ state: 'ASKING_PERMISSION' })

      try {
        await navigator.mediaDevices.getUserMedia({ video: false, audio: true })
      } catch (e) {
        captureTwilioWidgetError(e, {
          error_source: 'microphone_permission_denied',
          browser_permission: 'microphone',
          has_active_call: false,
        })
        setTwilioState({ state: 'PERMISSION_DENIED' })
        return
      }

      const device = await setupTwilioContext().catch((error) => {
        handleSetupError(error, 'device_setup_failed')
        return null
      })
      if (!device) return

      setTwilioState({
        state: 'SELECT_IO_DEVICES',
        onContinue: () => makeCall(device, `+61${number}`, forConsultation),
      })
    } else {
      makeCall(twilioContext.device, `+61${number}`, forConsultation)
    }
  }

  async function resetCallState() {
    const currentCall = useStore.getState().twilioContext?.call
    if (!currentCall) return

    setTwilioCallState(null)
    setTwilioState({ state: 'HIDDEN' })

    if (onCallEnd) {
      await onCallEnd()
    } else {
      // I've encountered some issues of async functions being weird when some
      // branch of the function does not contain any await. So here is an
      // await that does nothing.
      await new Promise((resolve) => resolve(null))
    }
  }

  async function disconnectCall(call: Call) {
    if (call.status() !== Call.State.Closed) {
      call.disconnect()
    }
    call.removeAllListeners()
    await resetCallState()
  }

  async function endCall() {
    if (
      twilioContext &&
      twilioContext.call &&
      twilioContext.call.status !== 'CONNECTING'
    ) {
      try {
        await disconnectCall(twilioContext.call.call)
      } catch (error) {
        captureTwilioWidgetError(error, {
          error_source: 'end_call_failed',
          call_status: twilioContext.call.call.status(),
          device_state: twilioContext.device.state,
          has_active_call: true,
        })
      }
    }
  }

  function handleSetupError(error: any, errorSource: TwilioWidgetErrorSource) {
    captureTwilioWidgetError(error, {
      error_source: errorSource,
      has_active_call: !!useStore.getState().twilioContext?.call,
    })

    setTwilioState({ state: 'HIDDEN' })
    showNotification(
      new NotificationItem(
        'Unexpected error occurred',
        NotificationItemVariant.Error,
      ),
    )
  }

  function makeCall(device: Device, number: string, forConsultation: string) {
    setTwilioCallState({ status: 'CONNECTING' })
    setTwilioState({ state: 'CALL_SCREEN' })

    recordCallMutation.mutate(forConsultation, {
      onSuccess: () => {
        device
          .connect({ params: { to: number } })
          .then((call) => {
            call.on('ringing', () =>
              setTwilioCallState({
                call,
                muted: false,
                status: 'CONNECTED',
                startTime: Date.now(),
              }),
            )
            call.on('reconnecting', () => {})
            call.on('reject', async () => {
              captureTwilioWidgetError(new Error('Twilio call rejected'), {
                error_source: 'call_rejected',
                call_status: call.status(),
                device_state: device.state,
                has_active_call: true,
              })
              showNotification(
                new NotificationItem(
                  'Your call was rejected',
                  NotificationItemVariant.Error,
                ),
              )

              await disconnectCall(call)
            })
            call.on('disconnect', async () => {
              await disconnectCall(call)
            })
            call.on('error', async (error) => {
              captureTwilioWidgetError(error, {
                error_source: 'call_error',
                call_status: call.status(),
                device_state: device.state,
                has_active_call: true,
              })
              showNotification(
                new NotificationItem(
                  `Unexpected error occurred: ${error}`,
                  NotificationItemVariant.Error,
                ),
              )
              await disconnectCall(call)
            })
          })
          .catch((err) => {
            handleSetupError(err, 'device_connect_failed')
          })
      },
      onError: (err) => {
        handleSetupError(err, 'record_call_failed')
      },
    })
  }

  function changeMuteStatus(shouldMute: boolean) {
    if (
      twilioContext &&
      twilioContext.call &&
      twilioContext.call.status !== 'CONNECTING'
    ) {
      try {
        twilioContext.call.call.mute(shouldMute)
        setTwilioCallState({
          ...twilioContext.call,
          muted: shouldMute,
        })
      } catch (error) {
        captureTwilioWidgetError(error, {
          error_source: shouldMute ? 'mute_call_failed' : 'unmute_call_failed',
          call_status: twilioContext.call.call.status(),
          device_state: twilioContext.device.state,
          has_active_call: true,
        })
      }
    }
  }

  function hideWidget() {
    setTwilioState({ state: 'HIDDEN' })
  }

  return {
    twilioState,
    hideWidget,
    call,
    endCall,
    allowCall,
    twilioContext,
    muteCall: () => changeMuteStatus(true),
    unmuteCall: () => changeMuteStatus(false),
  }
}
